import { FC, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useSlideInScrollAnchor } from 'lib/hooks/useSlideInScrollAnchor';
import {
  Button,
  InlineNotification,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Tile,
} from '@carbon/react';
import {
  CheckmarkOutline,
  Edit,
  Information,
  TrashCan,
  Undo,
} from '@carbon/react/icons';
import { StatusTag } from 'components/status-tag';
import { fmtAmount } from '../format';
import {
  confirmLineItemAtom,
  removeLineItemAtom,
  restoreLineItemAtom,
  revertLineItemAtom,
} from './atoms/line-items';
import {
  READ_ONLY_LINE_ITEM_HEADERS,
  renderReadOnlyLineItemCells,
} from './line-item-cells';
import { LineItemEditPanel } from './line-item-edit-panel';
import { VarianceGlossaryPanel } from './variance-glossary-panel';
import type { LineItemGroup, LineItemRow } from './types-line-item';
import shell from 'pages/afe-catalog/structuring/structuring-module.module.scss';
import local from './line-item-review.module.scss';

interface Props {
  documentId: number | string;
  // Position in the overview list, for the "Well N • …" header.
  index: number;
  group: LineItemGroup;
  // Reload the overview atom the shell drives this drill-in from — a mutation
  // re-derives this well's group + rollups on the next render.
  onMutated: () => void;
}

// Read-only columns + an Actions column (inline edit/delete icons), mirroring the
// mock's drill-in table (no per-line Status column — status is per-well here).
const COLUMNS = [...READ_ONLY_LINE_ITEM_HEADERS, 'Actions'];

export const LineItemReviewDetail: FC<Props> = ({
  documentId,
  index,
  group,
  onMutated,
}) => {
  const confirmLineItem = useSetAtom(confirmLineItemAtom);
  const revertLineItem = useSetAtom(revertLineItemAtom);
  const removeLineItem = useSetAtom(removeLineItemAtom);
  const restoreLineItem = useSetAtom(restoreLineItemAtom);

  // `editRow` holds the row under edit; `editOpen` drives the panel's open state
  // SEPARATELY so closing toggles open → false rather than unmounting the panel.
  // Carbon's slideIn SidePanel resets the page-content margin it adds only on the
  // open → false transition — unmounting while still open leaves the structuring
  // shell with a leftover right-margin (the main body never reverts). Mirrors the
  // Well edit panel (wells-overview-section).
  const [editRow, setEditRow] = useState<LineItemRow | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Delete is destructive → confirm in a modal first (soft-delete makes it
  // recoverable, but the confirm dialog is the app norm for a destructive action).
  const [deleteRow, setDeleteRow] = useState<LineItemRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [glossaryOpen, setGlossaryOpen] = useState(false);

  const rows = group.lineItems;
  const isComplete = group.status === 'complete';

  // Hold the section's viewport position while the slide-in edit panel animates
  // open/close (it sets margin-inline-end on the shell, reflowing every section).
  const rootRef = useRef<HTMLDivElement>(null);
  const anchorTopRef = useRef<number | null>(null);
  const captureAnchor = () => {
    anchorTopRef.current = rootRef.current?.getBoundingClientRect().top ?? null;
  };
  useSlideInScrollAnchor(editOpen, rootRef, anchorTopRef);

  const openEdit = (row: LineItemRow) => {
    captureAnchor();
    setEditRow(row);
    setEditOpen(true);
  };
  const closeEdit = () => {
    captureAnchor();
    setEditOpen(false);
  };

  const runAction = (
    setter: typeof confirmLineItem,
    href: string | undefined,
    method: 'POST' | 'DELETE'
  ) => {
    if (!href) return;
    setActionError(null);
    setter({
      url: href,
      method,
      onSuccess: () => onMutated(),
      onError: (m) => setActionError(m ?? 'Action failed.'),
    });
  };

  // Per-well confirm toggle (mock's "Mark as Correct"): mark every active line
  // correct, or clear the whole well's correct status. RC has no bulk endpoint,
  // so this fans the per-line confirm/revert over the well's lines.
  const setWellCorrect = async (correct: boolean) => {
    const targets = rows.filter((row) => {
      if (row.status === 'removed' || !row.resources.confirmation?.href)
        return false;
      return correct ? row.status !== 'confirmed' : row.status === 'confirmed';
    });
    if (targets.length === 0) return;

    setBusy(true);
    setActionError(null);
    const setter = correct ? confirmLineItem : revertLineItem;
    const method = correct ? 'POST' : 'DELETE';
    const results = await Promise.all(
      targets.map((row) =>
        setter({ url: row.resources.confirmation!.href, method })
      )
    );
    setBusy(false);
    if (results.some((res) => res.type !== 'success')) {
      setActionError('Some line items could not be updated. Please retry.');
    }
    onMutated();
  };

  const confirmDelete = () => {
    const url = deleteRow?.resources.detail?.href;
    if (!url) {
      setDeleteRow(null);
      return;
    }
    setDeleteError(null);
    removeLineItem({
      url,
      method: 'DELETE',
      onSuccess: () => {
        setDeleteRow(null);
        setDeleteError(null);
        onMutated();
      },
      onError: (m) => setDeleteError(m ?? 'Failed to delete the line.'),
    });
  };

  // Inline row actions (mirrors the Well section): Edit + Delete for an active
  // line, Undo for a removed one.
  const renderActions = (row: LineItemRow) => {
    if (row.status === 'removed') {
      return (
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Undo}
          iconDescription="Undo Remove"
          tooltipPosition="left"
          onClick={() =>
            runAction(restoreLineItem, row.resources.restoration?.href, 'POST')
          }
        />
      );
    }
    return (
      <div className={shell.rowActions}>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Edit}
          iconDescription="Edit"
          tooltipPosition="left"
          onClick={() => openEdit(row)}
        />
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={TrashCan}
          iconDescription="Delete Line Item"
          tooltipPosition="left"
          onClick={() => setDeleteRow(row)}
        />
      </div>
    );
  };

  return (
    <div
      className={`${shell.overviewBody} ${local.detailWhiteRows}`}
      ref={rootRef}
    >
      <div className={shell.overviewSectionTitleRow}>
        <h4 className={shell.overviewSectionTitle}>
          Well {index + 1} • {group.wellName ?? 'Unassigned'}
        </h4>
        <StatusTag status={group.status} />
        <div className={shell.rowActions} style={{ marginLeft: 'auto' }}>
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={Information}
            iconDescription="Variance Glossary"
            tooltipPosition="bottom"
            tooltipAlignment="end"
            onClick={() => setGlossaryOpen((v) => !v)}
          />
          <Button
            kind="primary"
            size="sm"
            renderIcon={CheckmarkOutline}
            disabled={busy || rows.length === 0}
            onClick={() => setWellCorrect(!isComplete)}
          >
            {isComplete ? 'Remove Correct Status' : 'Mark as Correct'}
          </Button>
        </div>
      </div>

      <p className={shell.sectionDesc}>
        Review the extracted royalty line items for this well. Edit values as
        needed, then Mark as Correct once verified.
      </p>

      <div className={shell.summaryTiles}>
        <Tile>
          <p className={shell.tileCount}>{fmtAmount(group.totalOwnerValue)}</p>
          <p className={shell.tileLabel}>Total Owner Value</p>
        </Tile>
        <Tile>
          <p className={shell.tileCount}>{group.lineItemCount}</p>
          <p className={shell.tileLabel}># Line Items</p>
        </Tile>
        <Tile>
          <p className={shell.tileCount}>
            {group.products.length ? group.products.join(', ') : '—'}
          </p>
          <p className={shell.tileLabel}>Products</p>
        </Tile>
      </div>

      {actionError && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error"
          subtitle={actionError}
          onCloseButtonClick={() => setActionError(null)}
        />
      )}

      {rows.length === 0 ? (
        <p>
          No line items for this well. Line items are derived from the wells
          acknowledged in Well Information.
        </p>
      ) : (
        <TableContainer>
          <Table size="sm">
            <TableHead>
              <TableRow>
                {COLUMNS.map((header) => (
                  <TableHeader key={header}>{header}</TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={
                    row.status === 'removed' ? shell.rowRemoved : undefined
                  }
                >
                  {renderReadOnlyLineItemCells(row)}
                  <TableCell>{renderActions(row)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editRow && (
        <LineItemEditPanel
          open={editOpen}
          documentId={documentId}
          row={editRow}
          onClose={closeEdit}
          onSaved={() => {
            closeEdit();
            onMutated();
          }}
        />
      )}

      <Modal
        open={deleteRow !== null}
        danger
        size="sm"
        modalHeading="Delete Line Item"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestClose={() => {
          setDeleteRow(null);
          setDeleteError(null);
        }}
        onSecondarySubmit={() => {
          setDeleteRow(null);
          setDeleteError(null);
        }}
        onRequestSubmit={confirmDelete}
      >
        <p>
          Are you sure you want to delete{' '}
          {deleteRow?.product ?? deleteRow?.propertyName ?? 'this line item'}?
          You can undo this from the removed line&rsquo;s Restore action.
        </p>
        {deleteError && (
          <InlineNotification
            kind="error"
            lowContrast
            title="Error"
            subtitle={deleteError}
            onCloseButtonClick={() => setDeleteError(null)}
            className={shell.modalErrorNotification}
          />
        )}
      </Modal>

      <VarianceGlossaryPanel
        open={glossaryOpen}
        onRequestClose={() => setGlossaryOpen(false)}
      />
    </div>
  );
};
